import VideoControlLayout, { VideoControlButtonWrapper, VideoWrapper } from "../VideoControls/VideoControlLayout";
import VideoCard, { VideoCardWrapper } from "../VideoLayout/VideoCard";
import VideoJS, { VideoJSProps } from "../VideoJS/VideoJS";
import {
	FullScreenButton,
	MoreOptionsButton,
	PlayButton,
	Seekbar,
	VideoSpeedButton,
	VideoTimeStamp,
	VolumeControls,
} from "../VideoControls/VideoControls";
import { formatEventTimestamp } from "../../../ProctoringResultsPage/ProctoringEventsTable";
import { SeekbarEvents } from "../SeekbarEvents/SeekbarEvents";
import { ChangeEvent, useRef, useState } from "react";
import PlayOverlay from "../overlay/PlayOverlay";
import { AutomatedProctoringSessionNote } from "../hooks/useSessionNotes";

// Can definitely clean this up -> see if i can use the VideoPlayer
type RoomScanPanelProps = {
	onPlayVideo: () => void;
	hasFinishedPlaying: boolean;
	isOverlayShowing: boolean;
	volumeProps: {
		showVolume: boolean;
		onVolumeButtonClick: () => void;
		onVolumeInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
	};
	maxDuration?: number;
	notes: AutomatedProctoringSessionNote[];
	onVideoSpeedChange: (speed: number) => void;
	onFullScreenClick: () => void;
	hasInstituteEditPermission: boolean;
	sessionStatus?: string | null;
	onDeleteRecording: () => void;
} & Pick<
	VideoJSProps,
	| "dispatch"
	| "options"
	| "controls"
	| "startDate"
	| "arrStamps"
	| "sources"
	| "videoTimer"
	| "displayStateAtom"
	| "toggledTimestamps"
	| "isFullscreen"
	| "setVideoDuration"
	| "isDeleted"
	| "deleteCause"
	| "videoHasError"
	| "setVideoHasError"
>;

export default function RoomScanPanel({
	dispatch,
	options,
	controls,
	startDate,
	arrStamps,
	sources,
	videoTimer,
	displayStateAtom,
	toggledTimestamps: showTimestamps,
	isFullscreen,
	setVideoDuration,
	isDeleted,
	deleteCause,
	videoHasError,
	setVideoHasError,
	onPlayVideo,
	hasFinishedPlaying,
	isOverlayShowing,
	volumeProps,
	maxDuration,
	notes,
	onVideoSpeedChange,
	onFullScreenClick,
	hasInstituteEditPermission,
	sessionStatus,
	onDeleteRecording,
}: RoomScanPanelProps) {
	const [moreOptionShowing, setMoreOptionShowing] = useState(false);
	const [videoSpeedShowing, setVideoSpeedShowing] = useState(false);

	const controlContainerRef = useRef<HTMLDivElement>(null);

	const showMoreOptions =
		Object.entries(sources).length > 0 &&
		!isDeleted &&
		!isFullscreen &&
		hasInstituteEditPermission &&
		sessionStatus !== "incomplete";
	return (
		<VideoWrapper inFullscreen={isFullscreen} showTimestamps={showTimestamps}>
			<VideoCardWrapper>
				<VideoCard onClick={onPlayVideo}>
					<VideoJS
						events={[]}
						debugging_string={"webcam"}
						dispatch={dispatch}
						options={options}
						controls={controls}
						startDate={startDate}
						arrStamps={arrStamps}
						sources={sources}
						videoTimer={videoTimer}
						displayStateAtom={displayStateAtom}
						toggledTimestamps={showTimestamps}
						isFullscreen={isFullscreen}
						setVideoDuration={setVideoDuration}
						isDeleted={isDeleted}
						deleteCause={deleteCause}
						videoHasError={videoHasError}
						setVideoHasError={setVideoHasError}
					/>
				</VideoCard>
				{!videoHasError && (
					<PlayOverlay
						isOverlayVisible={isOverlayShowing}
						useVideoEndedOverlay={hasFinishedPlaying}
						setPlay={onPlayVideo}
					/>
				)}
			</VideoCardWrapper>
			{sources && (
				<VideoControlLayout inFullscreen={isFullscreen} controlContainerRef={controlContainerRef}>
					<PlayButton
						isPlaying={controls.isPlaying}
						onClick={() => {
							if (!videoHasError) {
								onPlayVideo();
							}
						}}
						inFullscreen={isFullscreen}
					/>
					<VolumeControls
						volume={controls.volume}
						showVolumeInput={volumeProps.showVolume}
						onVolumeButtonClick={volumeProps.onVolumeButtonClick}
						onVolumeInputChange={volumeProps.onVolumeInputChange}
						inFullscreen={isFullscreen}
					/>
					<VideoTimeStamp inFullscreen={isFullscreen}>
						{`${formatEventTimestamp(videoTimer.timer, maxDuration)}/${formatEventTimestamp(maxDuration ?? 0, maxDuration)}`}
					</VideoTimeStamp>
					<Seekbar
						curSeekbarValue={videoTimer.timer}
						minSeekbarValue={0}
						maxSeekbarValue={maxDuration}
						onSeekbarChange={(e) => {
							dispatch({
								type: "skip",
								time: e.target.valueAsNumber,
							});
						}}
						inFullscreen={isFullscreen}
					>
						<SeekbarEvents
							events={[]}
							sessionNotes={notes}
							liveNotes={[]}
							dispatch={dispatch}
							duration={maxDuration}
							globaltime={videoTimer.timer / 1000}
							skip={videoTimer.skip}
							offsetGetter={0}
							isFullscreen={isFullscreen}
						/>
					</Seekbar>
					<VideoControlButtonWrapper>
						<VideoSpeedButton
							inFullscreen={isFullscreen}
							menuShowing={videoSpeedShowing}
							setMenuShowing={setVideoSpeedShowing}
							playbackSpeed={controls.playbackSpeed}
							controlContainerRef={controlContainerRef}
							onSelectSpeed={onVideoSpeedChange}
						/>
						<FullScreenButton inFullscreen={isFullscreen} onClick={onFullScreenClick} />
						{showMoreOptions && (
							<MoreOptionsButton
								inFullscreen={isFullscreen}
								menuShowing={moreOptionShowing}
								setMenuShowing={setMoreOptionShowing}
								onDeleteRecording={onDeleteRecording}
							/>
						)}
					</VideoControlButtonWrapper>
				</VideoControlLayout>
			)}
		</VideoWrapper>
	);
}
